// src/components/admin/SponsorSettingsEditor.js
//
// スポンサー掲載の管理UI。3つのタブで構成される。
//   (1) 申込台帳   … 承認 → 入金確認 → 掲載開始 → 紹介報酬
//   (2) 掲載データ … アプリに出る内容の編集
//   (3) 表示設定   … 文言・カテゴリ・報酬額の設定
//
// 【重要・保存の分離について】
//   このエディタは settings/config ではなく settings/sponsors に保存する。
//   Admin.js の saveSettings() は state の全項目を一度に setDoc するため、
//   同じドキュメントに入れると、期限切れ処理（Functions）が active を
//   書き換えた直後に管理者が「設定をすべて保存」を押しただけで
//   その変更が巻き戻ってしまう。
//   → 保存ボタンもこのセクション専用のものを使うこと。
//
// 【掲載可否の即時切替について】
//   「掲載中／停止中」のトグルだけは、保存ボタンを待たずにその場で書き込む。
//   問題のある企業を急いで下げたいときに、保存忘れで反映されないと
//   意味がないため。他の編集項目とは扱いを分けている。
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import {
    doc, getDoc, setDoc, collection, getDocs, updateDoc,
    addDoc, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    DEFAULT_SPONSOR_CATEGORIES, DEFAULT_REWARD_AMOUNT, mergeTexts
} from '../../sponsorDefaults';

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 掲載開始日から1年後の前日を返す（＝ちょうど1年間の掲載）
const oneYearLater = (fromIso) => {
    const d = new Date(fromIso + 'T00:00:00');
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const STATUS_LABEL = {
    pending:   { text: '未審査',           bg: '#fff3e0', color: '#e65100' },
    approved:  { text: '承認済(入金待ち)', bg: '#e3f2fd', color: '#1565c0' },
    paid:      { text: '入金確認済',       bg: '#e8f5e9', color: '#2e7d32' },
    published: { text: '掲載中',           bg: '#e8f5e9', color: '#1b5e20' },
    rejected:  { text: '却下',             bg: '#fafafa', color: '#999' },
};

const Field = ({ label, value, onChange, placeholder, type = 'text', hint }) => (
    <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>{label}</label>
        <input
            type={type}
            value={value === undefined || value === null ? '' : value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        {hint && <div style={{ fontSize: '10px', color: '#999', marginTop: '3px', lineHeight: '1.6' }}>{hint}</div>}
    </div>
);

const TextArea = ({ label, value, onChange, hint, rows = 3 }) => (
    <div style={{ marginBottom: '14px' }}>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>{label}</label>
        <textarea
            value={value === undefined || value === null ? '' : value}
            onChange={e => onChange(e.target.value)}
            rows={rows}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', lineHeight: '1.7', resize: 'vertical' }}
        />
        {hint && <div style={{ fontSize: '10px', color: '#999', marginTop: '3px', lineHeight: '1.6' }}>{hint}</div>}
    </div>
);

const SponsorSettingsEditor = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const [enabled, setEnabled] = useState(true);
    const [categories, setCategories] = useState(DEFAULT_SPONSOR_CATEGORIES);
    const [items, setItems] = useState([]);
    const [rewardAmount, setRewardAmount] = useState(DEFAULT_REWARD_AMOUNT);
    const [texts, setTexts] = useState(mergeTexts(null));
    const [apps, setApps] = useState([]);
    const [view, setView] = useState('apps');   // 'apps' | 'items' | 'config'

    const [newCatLabel, setNewCatLabel] = useState('');
    // 掲載タブのアコーディオン。件数が増えても一覧が縦に伸びないよう、
    //   既定は全て折りたたみ、開くのは1件だけにする。
    const [expandedId, setExpandedId] = useState(null);
    const [itemFilter, setItemFilter] = useState('');

    const load = async () => {
        setIsLoading(true);
        try {
            const snap = await getDoc(doc(db, 'settings', 'sponsors'));
            if (snap.exists()) {
                const d = snap.data();
                setEnabled(d.enabled !== false);
                setCategories(Array.isArray(d.categories) && d.categories.length ? d.categories : DEFAULT_SPONSOR_CATEGORIES);
                setItems(Array.isArray(d.items) ? d.items : []);
                setRewardAmount(typeof d.rewardAmount === 'number' ? d.rewardAmount : DEFAULT_REWARD_AMOUNT);
                setTexts(mergeTexts(d.texts));
            }
            const q = query(collection(db, 'sponsor_applications'), orderBy('createdAt', 'desc'));
            const as = await getDocs(q);
            setApps(as.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsDirty(false);
        } catch (e) {
            console.error(e);
            alert('スポンサー情報の読み込みに失敗しました。コンソールをご確認ください。');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { if (isOpen) load(); /* eslint-disable-next-line */ }, [isOpen]);

    // ---- 保存の共通処理 ------------------------------------------------
    //   override で一部だけ差し替えられるようにしている。
    //   （state の更新は非同期なので、即時トグルでは新しい値を明示的に渡す）
    const persist = async (override = {}) => {
        const payload = {
            enabled, categories, items, rewardAmount, texts,
            updatedAt: new Date().toISOString(),
            ...override
        };
        await setDoc(doc(db, 'settings', 'sponsors'), payload, { merge: true });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await persist();
            setIsDirty(false);
            alert('✅ 保存しました');
        } catch (e) {
            console.error(e);
            alert('❌ 保存に失敗しました。権限（運営メンバー設定）をご確認ください。');
        } finally {
            setIsSaving(false);
        }
    };

    // ---- ★掲載可否の即時切替（保存ボタン不要） --------------------------
    const handleToggleActive = async (idx) => {
        const it = items[idx];
        const nextActive = it.active === false;   // 現在OFFならON、ONならOFF

        if (!nextActive) {
            if (!window.confirm(`「${it.name}」をアプリ上で今すぐ非表示にします。\n\nよろしいですか？`)) return;
        }
        if (isDirty && !window.confirm('未保存の編集があります。\nそれらも一緒に保存されますが、よろしいですか？')) return;

        const next = items.map((x, i) => i === idx ? { ...x, active: nextActive } : x);
        setItems(next);
        try {
            await persist({ items: next });
            setIsDirty(false);
            alert(nextActive ? '✅ 掲載を再開しました' : '⛔ 非表示にしました（即時反映）');
        } catch (e) {
            console.error(e);
            alert('❌ 切替に失敗しました。もう一度お試しください。');
            setItems(items);   // 失敗したら元に戻す
        }
    };

    // ---- 申込の審査 ----------------------------------------------------
    const setStatus = async (app, status, extra = {}) => {
        try {
            await updateDoc(doc(db, 'sponsor_applications', app.id), { status, ...extra });
            setApps(prev => prev.map(a => a.id === app.id ? { ...a, status, ...extra } : a));
        } catch (e) {
            console.error(e);
            alert('❌ 更新に失敗しました');
        }
    };

    const handleApprove = (app) => {
        if (!window.confirm(`「${app.companyName}」を承認します。\n\n承認後、振込のご案内を送ってください。`)) return;
        setStatus(app, 'approved', { approvedAt: new Date().toISOString() });
    };

    const handleReject = (app) => {
        const reason = window.prompt('却下の理由（任意・申込者には表示されません）', '');
        if (reason === null) return;
        setStatus(app, 'rejected', { rejectedAt: new Date().toISOString(), rejectReason: reason || '' });
    };

    // 入金確認 → 掲載データを生成して settings/sponsors に追加する
    const handleMarkPaid = async (app) => {
        const amountStr = window.prompt('入金額を入力してください（円）', String(app.amount || 10000));
        if (amountStr === null) return;
        const amount = Number(amountStr);
        if (!Number.isFinite(amount) || amount < 0) { alert('金額が正しくありません'); return; }

        const from = window.prompt('掲載開始日（YYYY-MM-DD）', todayIso());
        if (!from) return;
        const until = oneYearLater(from);

        if (!window.confirm(`「${app.companyName}」を掲載します。\n\n入金額: ${amount.toLocaleString()}円\n掲載期間: ${from} 〜 ${until}\n\nよろしいですか？`)) return;

        const newItem = {
            id: `sp_${Date.now()}`,
            name: app.companyName || '',
            categoryId: app.categoryId || 'other',
            logoUrl: app.logoUrl || '',
            catch: app.prText || '',
            url: app.url || '',
            couponCode: app.couponCode || '',
            couponDesc: app.couponPlan || '',
            couponNote: '',
            validFrom: from,
            validUntil: until,
            active: true,
            order: (items.length + 1) * 10,
            applicationId: app.id
        };
        const next = [...items, newItem];
        setItems(next);

        try {
            await persist({ items: next });
            await updateDoc(doc(db, 'sponsor_applications', app.id), {
                status: 'published',
                paidAt: new Date().toISOString(),
                amount,
                validFrom: from,
                validUntil: until,
                sponsorId: newItem.id
            });
            setApps(prev => prev.map(a => a.id === app.id
                ? { ...a, status: 'published', amount, validFrom: from, validUntil: until, sponsorId: newItem.id }
                : a));
            setIsDirty(false);
            alert('✅ 掲載を開始しました。\n\n続けて「掲載データ」タブでロゴ画像やキャッチコピーを整えてください。');
        } catch (e) {
            console.error(e);
            alert('❌ 掲載処理に失敗しました。コンソールをご確認ください。');
            load();
        }
    };

    // ---- 紹介者へのRBコイン付与 ----------------------------------------
    //   ⚠ points はルールで本人書き込み禁止・全処理が Functions に集約されている。
    //     クライアントから直接 points を書くと point_logs（通帳）との突合が
    //     崩れるため、必ず Cloud Functions 経由で付与する。
    const handleReward = async (app) => {
        if (!app.applicantId) { alert('紹介者の情報がありません'); return; }
        if (app.rewardPaid) { alert('この申込には既に報酬を付与済みです'); return; }

        // 「表示設定」で決めた既定額を初期値として出す（その場で変更も可能）
        const amountStr = window.prompt('紹介者へ付与するRBコイン数', String(rewardAmount));
        if (!amountStr) return;
        const amount = Number(amountStr);
        if (!Number.isFinite(amount) || amount <= 0) { alert('数値が正しくありません'); return; }

        if (!window.confirm(`紹介者（${app.applicantName || app.applicantId}）に ${amount} RB を付与します。\n\nよろしいですか？`)) return;

        try {
            const fns = getFunctions(getApp(), 'asia-northeast1');
            await httpsCallable(fns, 'grantSponsorReward')({ applicationId: app.id, amount });
            setApps(prev => prev.map(a => a.id === app.id ? { ...a, rewardPaid: true, rewardAmount: amount } : a));
            alert('✅ 付与しました');
        } catch (e) {
            console.error(e);
            alert('❌ 付与に失敗しました。\n\ngrantSponsorReward がデプロイされているかご確認ください。');
        }
    };

    // ---- 管理者による直接登録（申込台帳を経由する） ----------------------
    const handleManualAdd = async () => {
        const companyName = window.prompt('会社名・店舗名を入力してください');
        if (!companyName) return;
        try {
            const ref = await addDoc(collection(db, 'sponsor_applications'), {
                applicantId: 'admin',
                applicantName: '管理者による代理登録',
                companyName,
                contactName: '', email: '', phone: '',
                categoryId: 'other', prText: '', couponPlan: '', transferName: '', url: '',
                source: 'admin',
                status: 'approved',
                createdAt: serverTimestamp(),
                approvedAt: new Date().toISOString()
            });
            alert('✅ 申込台帳に追加しました。\n\n続けて「入金確認して掲載」を押すと掲載されます。');
            setApps(prev => [{ id: ref.id, companyName, applicantName: '管理者による代理登録', status: 'approved', source: 'admin' }, ...prev]);
        } catch (e) {
            console.error(e);
            alert('❌ 追加に失敗しました');
        }
    };

    // ---- 掲載データの編集 ----------------------------------------------
    const updateItem = (idx, key, value) => {
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
        setIsDirty(true);
    };
    const removeItem = (idx) => {
        if (!window.confirm(`「${items[idx].name}」を掲載から削除します。\n\n※申込台帳の記録は残ります。\n※削除後、保存ボタンを押すまで確定しません。`)) return;
        setItems(prev => prev.filter((_, i) => i !== idx));
        setIsDirty(true);
    };

    // ---- カテゴリの追加・削除 ------------------------------------------
    const addCategory = () => {
        const label = newCatLabel.trim();
        if (!label) { alert('カテゴリ名を入力してください'); return; }
        if (categories.some(c => c.label === label)) { alert('同じ名前のカテゴリが既にあります'); return; }
        const id = `cat_${Date.now()}`;
        const maxOrder = categories.reduce((m, c) => Math.max(m, c.order ?? 0), 0);
        setCategories(prev => [...prev, { id, label, order: maxOrder + 1 }]);
        setNewCatLabel('');
        setIsDirty(true);
    };

    const removeCategory = (id) => {
        const cat = categories.find(c => c.id === id);
        const used = items.filter(it => it.categoryId === id);
        if (used.length > 0) {
            const names = used.map(u => u.name).join(' / ');
            if (!window.confirm(`「${cat.label}」は ${used.length} 件のスポンサーで使われています。\n\n${names}\n\n削除すると、これらは「その他」に移動します。\nよろしいですか？`)) return;
            // 「その他」が無ければ作る
            const hasOther = categories.some(c => c.id === 'other');
            setItems(prev => prev.map(it => it.categoryId === id ? { ...it, categoryId: 'other' } : it));
            if (!hasOther) {
                setCategories(prev => [...prev.filter(c => c.id !== id), { id: 'other', label: 'その他', order: 99 }]);
                setIsDirty(true);
                return;
            }
        } else {
            if (!window.confirm(`カテゴリ「${cat.label}」を削除します。よろしいですか？`)) return;
        }
        setCategories(prev => prev.filter(c => c.id !== id));
        setIsDirty(true);
    };

    const updateCategoryLabel = (id, label) => {
        setCategories(prev => prev.map(c => c.id === id ? { ...c, label } : c));
        setIsDirty(true);
    };

    const setText = (key, value) => {
        setTexts(prev => ({ ...prev, [key]: value }));
        setIsDirty(true);
    };

    const pendingCount = apps.filter(a => a.status === 'pending').length;

    return (
        <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', background: 'white', overflow: 'hidden' }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    padding: '15px', background: '#fff3e0', cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', color: '#e65100'
                }}
            >
                <span>
                    🎫 スポンサー・提携パートナー
                    {pendingCount > 0 && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', background: '#d32f2f', color: 'white', padding: '2px 8px', borderRadius: '10px' }}>
                            未審査 {pendingCount}
                        </span>
                    )}
                </span>
                <span>{isOpen ? '▲ 閉じる' : '▼ 開く'}</span>
            </div>

            {isOpen && (
                <div style={{ padding: '15px' }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '30px' }}>読み込み中...</div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '15px', padding: '10px', background: '#f1f8e9', borderRadius: '8px', border: '1px solid #c5e1a5' }}>
                                <label style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#33691e', fontSize: '13px' }}>
                                    <input
                                        type="checkbox"
                                        checked={enabled}
                                        onChange={e => { setEnabled(e.target.checked); setIsDirty(true); }}
                                        style={{ width: 'auto', marginRight: '8px', transform: 'scale(1.2)' }}
                                    />
                                    スポンサー一覧を会員に表示する
                                </label>
                            </div>

                            {isDirty && (
                                <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', fontSize: '11px', color: '#e65100', fontWeight: 'bold' }}>
                                    ⚠ 未保存の変更があります。下部の保存ボタンを押してください。
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '6px', marginBottom: '15px' }}>
                                {[['apps', `📋 申込 (${apps.length})`], ['items', `🏪 掲載 (${items.length})`], ['config', '⚙️ 表示設定']].map(([k, label]) => (
                                    <button
                                        key={k}
                                        onClick={() => setView(k)}
                                        style={{
                                            flex: 1, padding: '10px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                                            fontWeight: 'bold', fontSize: '11px', whiteSpace: 'nowrap',
                                            background: view === k ? '#ff9800' : '#f5f5f5',
                                            color: view === k ? 'white' : '#666'
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* ============ 申込台帳 ============ */}
                            {view === 'apps' && (
                                <div>
                                    <button
                                        onClick={handleManualAdd}
                                        className="btn"
                                        style={{ width: '100%', marginBottom: '15px', background: '#f5f5f5', border: '1px dashed #bbb', padding: '10px', fontSize: '13px' }}
                                    >
                                        ＋ 管理者が代理で登録する
                                    </button>

                                    {apps.length === 0 && (
                                        <div style={{ textAlign: 'center', color: '#999', padding: '30px', fontSize: '13px' }}>
                                            申込はまだありません
                                        </div>
                                    )}

                                    {apps.map(app => {
                                        const st = STATUS_LABEL[app.status] || STATUS_LABEL.pending;
                                        return (
                                            <div key={app.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: '#fafafa' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>{app.companyName}</span>
                                                    <span style={{ fontSize: '10px', background: st.bg, color: st.color, padding: '3px 10px', borderRadius: '10px', fontWeight: 'bold' }}>
                                                        {st.text}
                                                    </span>
                                                </div>

                                                <div style={{ fontSize: '11px', color: '#777', lineHeight: '1.8', marginBottom: '10px' }}>
                                                    {app.contactName && <div>担当: {app.contactName}</div>}
                                                    {app.email && <div>メール: {app.email}</div>}
                                                    {app.phone && <div>電話: {app.phone}</div>}
                                                    {app.transferName && <div>振込名義: {app.transferName}</div>}
                                                    {app.couponPlan && <div>希望クーポン: {app.couponPlan}</div>}
                                                    <div style={{ color: '#aaa' }}>紹介者: {app.applicantName || app.applicantId || '—'}</div>
                                                    {app.validUntil && <div style={{ color: '#2e7d32' }}>掲載期限: {app.validUntil}</div>}
                                                    {app.rewardPaid && <div style={{ color: '#1565c0' }}>紹介報酬: 付与済み ({app.rewardAmount} RB)</div>}
                                                </div>

                                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    {app.status === 'pending' && (
                                                        <>
                                                            <button onClick={() => handleApprove(app)} className="btn btn-sm" style={{ background: '#1976d2', color: 'white', border: 'none', fontSize: '11px', padding: '6px 12px' }}>承認する</button>
                                                            <button onClick={() => handleReject(app)} className="btn btn-sm" style={{ background: '#eee', color: '#666', border: 'none', fontSize: '11px', padding: '6px 12px' }}>却下</button>
                                                        </>
                                                    )}
                                                    {app.status === 'approved' && (
                                                        <button onClick={() => handleMarkPaid(app)} className="btn btn-sm" style={{ background: '#2e7d32', color: 'white', border: 'none', fontSize: '11px', padding: '6px 12px' }}>入金確認して掲載</button>
                                                    )}
                                                    {app.status === 'published' && !app.rewardPaid && app.applicantId && app.applicantId !== 'admin' && (
                                                        <button onClick={() => handleReward(app)} className="btn btn-sm" style={{ background: '#ff9800', color: 'white', border: 'none', fontSize: '11px', padding: '6px 12px' }}>
                                                            紹介者に {rewardAmount} RB 付与
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ============ 掲載データ ============ */}
                            {view === 'items' && (
                                <div>
                                    {items.length > 3 && (
                                        <input
                                            value={itemFilter}
                                            onChange={e => setItemFilter(e.target.value)}
                                            placeholder="社名で絞り込み"
                                            style={{ width: '100%', padding: '9px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}
                                        />
                                    )}

                                    {items.length === 0 && (
                                        <div style={{ textAlign: 'center', color: '#999', padding: '30px', fontSize: '13px' }}>
                                            掲載中のスポンサーはありません。<br />
                                            「申込」タブから入金確認を行うと、ここに追加されます。
                                        </div>
                                    )}

                                    {items.map((it, idx) => {
                                        const isOn = it.active !== false;
                                        const key = it.id || String(idx);
                                        const isExpanded = expandedId === key;
                                        // 検索にヒットしないものは描画しない（DOMを軽く保つ）
                                        const kw = itemFilter.trim();
                                        if (kw && !(it.name || '').includes(kw)) return null;
                                        return (
                                            <div key={it.id || idx} style={{
                                                border: isOn ? '1px solid #eee' : '2px solid #ef9a9a',
                                                borderRadius: '8px', padding: '12px', marginBottom: '12px',
                                                background: isOn ? '#fafafa' : '#ffebee'
                                            }}>
                                                {/* 常時表示のヘッダー行。ここだけで掲載可否を切り替えられる。 */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <button
                                                        onClick={() => handleToggleActive(idx)}
                                                        title="押すとその場でアプリに反映されます（保存不要）"
                                                        style={{
                                                            padding: '7px 10px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                                                            fontWeight: 'bold', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0,
                                                            background: isOn ? '#2e7d32' : '#d32f2f', color: 'white'
                                                        }}
                                                    >
                                                        {isOn ? '● 掲載中' : '■ 停止中'}
                                                    </button>
                                                    <div
                                                        onClick={() => setExpandedId(isExpanded ? null : key)}
                                                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                                                    >
                                                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {it.name || '(社名未設定)'}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#999' }}>
                                                            {(categories.find(c => c.id === it.categoryId) || {}).label || 'その他'}
                                                            {it.validUntil ? ` / 〜${it.validUntil}` : ''}
                                                            {it.couponCode ? ' / 🎫' : ''}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => setExpandedId(isExpanded ? null : key)}
                                                        style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px', color: '#666', fontSize: '11px', padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}
                                                    >
                                                        {isExpanded ? '▲ 閉じる' : '▼ 編集'}
                                                    </button>
                                                </div>

                                                {isExpanded && (<div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #ddd' }}>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                                    <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#d32f2f', fontSize: '11px', cursor: 'pointer' }}>この掲載を削除</button>
                                                </div>
                                                <Field label="社名・店舗名" value={it.name} onChange={v => updateItem(idx, 'name', v)} />
                                                <div style={{ marginBottom: '10px' }}>
                                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>カテゴリ</label>
                                                    <select
                                                        value={it.categoryId || 'other'}
                                                        onChange={e => updateItem(idx, 'categoryId', e.target.value)}
                                                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                    >
                                                        {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                                                    </select>
                                                </div>
                                                <Field label="ロゴ画像URL" value={it.logoUrl} onChange={v => updateItem(idx, 'logoUrl', v)} placeholder="https://.../logo.png" />
                                                <Field label="キャッチコピー（一言）" value={it.catch} onChange={v => updateItem(idx, 'catch', v)} placeholder="例: 選手の身体を支える整骨院" />
                                                <Field label="リンク先URL" value={it.url} onChange={v => updateItem(idx, 'url', v)} placeholder="https://..." />
                                                <Field label="クーポンコード" value={it.couponCode} onChange={v => updateItem(idx, 'couponCode', v)} placeholder="例: REVENGERS10" hint="空欄にするとクーポンボタンが出ません" />
                                                <Field label="クーポン内容" value={it.couponDesc} onChange={v => updateItem(idx, 'couponDesc', v)} placeholder="例: ご提示で施術料10%OFF" />
                                                <Field label="利用条件（注意書き）" value={it.couponNote} onChange={v => updateItem(idx, 'couponNote', v)} placeholder="例: 他券併用不可 / 1グループ1回まで" />

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                    <Field label="掲載開始日" type="date" value={it.validFrom} onChange={v => updateItem(idx, 'validFrom', v)} />
                                                    <Field label="掲載終了日" type="date" value={it.validUntil} onChange={v => updateItem(idx, 'validUntil', v)} />
                                                </div>
                                                <Field label="表示順（小さいほど上）" type="number" value={it.order} onChange={v => updateItem(idx, 'order', Number(v))} />
                                                </div>)}
                                            </div>
                                        );
                                    })}

                                    <button onClick={handleSave} disabled={isSaving} className="btn btn-primary" style={{ width: '100%', padding: '14px', marginTop: '10px' }}>
                                        {isSaving ? '保存中...' : '🎫 スポンサー掲載情報を保存'}
                                    </button>
                                    <div style={{ fontSize: '10px', color: '#999', textAlign: 'center', marginTop: '6px', lineHeight: '1.6' }}>
                                        ※ 画面下部の「設定をすべて保存」ではスポンサーは保存されません。
                                    </div>
                                </div>
                            )}

                            {/* ============ 表示設定 ============ */}
                            {view === 'config' && (
                                <div>
                                    {/* 紹介報酬 */}
                                    <div style={{ marginBottom: '20px', padding: '12px', background: '#fff8e1', borderRadius: '8px', border: '1px solid #ffe082' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#e65100' }}>💰 紹介報酬</div>
                                        <Field
                                            label="紹介者に付与するRBコイン（既定額）"
                                            type="number"
                                            value={rewardAmount}
                                            onChange={v => { setRewardAmount(Number(v) || 0); setIsDirty(true); }}
                                            hint="申込タブの付与ボタンに、この額が初期値として入ります。付与時にその場で変更もできます。0 にすると付与ボタンは出ますが既定額が0になります。"
                                        />
                                    </div>

                                    {/* カテゴリ */}
                                    <div style={{ marginBottom: '20px', padding: '12px', background: '#f3f8ff', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#1565c0' }}>🏷 カテゴリ</div>
                                        <div style={{ fontSize: '10px', color: '#777', marginBottom: '10px', lineHeight: '1.6' }}>
                                            掲載が1件もないカテゴリは、会員側のタブには表示されません。
                                        </div>

                                        {categories.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map(c => {
                                            const usedCount = items.filter(it => it.categoryId === c.id).length;
                                            return (
                                                <div key={c.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                                                    <input
                                                        value={c.label}
                                                        onChange={e => updateCategoryLabel(c.id, e.target.value)}
                                                        style={{ flex: 1, padding: '7px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
                                                    />
                                                    <span style={{ fontSize: '10px', color: '#999', minWidth: '38px', textAlign: 'right' }}>{usedCount}件</span>
                                                    <button
                                                        onClick={() => removeCategory(c.id)}
                                                        style={{ background: '#fff', border: '1px solid #ef9a9a', color: '#d32f2f', borderRadius: '4px', fontSize: '11px', padding: '6px 10px', cursor: 'pointer' }}
                                                    >
                                                        削除
                                                    </button>
                                                </div>
                                            );
                                        })}

                                        <div style={{ display: 'flex', gap: '6px', marginTop: '12px' }}>
                                            <input
                                                value={newCatLabel}
                                                onChange={e => setNewCatLabel(e.target.value)}
                                                placeholder="新しいカテゴリ名"
                                                style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
                                            />
                                            <button onClick={addCategory} className="btn btn-sm" style={{ background: '#1976d2', color: 'white', border: 'none', padding: '8px 16px', fontSize: '12px' }}>追加</button>
                                        </div>
                                    </div>

                                    {/* 特典ページの文言 */}
                                    <div style={{ marginBottom: '20px', padding: '12px', background: '#f1f8e9', borderRadius: '8px', border: '1px solid #c5e1a5' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#33691e' }}>🎫 特典ページの文言</div>
                                        <TextArea
                                            label="案内文（緑のバナー内）"
                                            value={texts.noticeText}
                                            onChange={v => setText('noticeText', v)}
                                            rows={2}
                                        />
                                        <TextArea
                                            label="注意書き（案内文の下・小さい文字）"
                                            value={texts.noticeCaution}
                                            onChange={v => setText('noticeCaution', v)}
                                            rows={2}
                                            hint="スクリーンショット偽造への注意喚起です。空欄にすると表示されません。"
                                        />
                                    </div>

                                    {/* 申込ページの文言 */}
                                    <div style={{ marginBottom: '20px', padding: '12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #ddd' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#555' }}>📝 申込ページの文言</div>
                                        <div style={{ fontSize: '10px', color: '#777', marginBottom: '12px', padding: '8px', background: '#fff', border: '1px dashed #ccc', borderRadius: '6px', lineHeight: '1.8' }}>
                                            <b>差し込み記号</b>（そのまま書くと自動で置き換わります）<br />
                                            <code>{'{memberCount}'}</code> … 現在の登録者数（実数）<br />
                                            <code>{'{memberCountRound}'}</code> … 10の位で切り捨て（約〜名と書くとき用）
                                        </div>
                                        <TextArea
                                            label="冒頭の説明"
                                            value={texts.applyIntro}
                                            onChange={v => setText('applyIntro', v)}
                                            rows={2}
                                        />
                                        <TextArea
                                            label="掲載条件（改行で複数行）"
                                            value={texts.applyDetails}
                                            onChange={v => setText('applyDetails', v)}
                                            rows={4}
                                            hint="掲載期間・掲載料・会員数など。改行がそのまま反映されます。{memberCount} と書くと現在の登録者数（実数）、{memberCountRound} と書くと10の位で切り捨てた数（例: 600）に置き換わります。"
                                        />
                                        <TextArea
                                            label="掲載場所（改行で複数行）"
                                            value={texts.applyPlacement}
                                            onChange={v => setText('applyPlacement', v)}
                                            rows={6}
                                            hint="申込ページの「掲載イメージ」欄に出ます。設置場所を増やしたときはここも直してください。"
                                        />
                                        <TextArea
                                            label="注意事項（改行で複数行）"
                                            value={texts.applyCautions}
                                            onChange={v => setText('applyCautions', v)}
                                            rows={4}
                                            hint="お断りする業種など。後から断ると角が立つので、明記しておくことをお勧めします。"
                                        />
                                    </div>

                                    <button onClick={handleSave} disabled={isSaving} className="btn btn-primary" style={{ width: '100%', padding: '14px' }}>
                                        {isSaving ? '保存中...' : '⚙️ 表示設定を保存'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default SponsorSettingsEditor;